import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Info } from "lucide-react";
import { api, errMsg, formatDate, rupiah } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { AdminHead, Table, Td } from "@/components/admin/AdminUI";
import { PageLoader } from "@/components/layout/RequireAuth";

const ProviderPayout = ({ p, onDone }) => {
  const [sel, setSel] = useState(p.transactions.map((t) => t.transaction_id));
  const [ref, setRef] = useState("");
  const total = p.transactions.filter((t) => sel.includes(t.transaction_id)).reduce((s, t) => s + t.provider_net, 0);
  const submit = async () => {
    try {
      await api.post("/admin/payouts", { provider_id: p.provider_id, transaction_ids: sel, reference: ref });
      toast.success("Ditandai sudah dicairkan");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-soft p-5" data-testid={`payout-provider-${p.provider_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-bold text-stone-900">{p.business_name}</p>
          <p className="text-xs text-stone-500" data-testid={`payout-bank-${p.provider_id}`}>{p.bank_name || "Bank belum diisi"} · {p.bank_account_number || "-"} a.n. {p.bank_account_name || "-"}</p>
        </div>
        <p className="text-right text-sm text-stone-500">Siap dicairkan<br /><b className="text-lg text-stone-900">{rupiah(p.total)}</b></p>
      </div>
      <div className="mt-4 max-h-56 divide-y divide-stone-100 overflow-y-auto rounded-xl border border-stone-100">
        {p.transactions.map((t) => (
          <label key={t.transaction_id} className="flex items-center gap-3 px-3 py-2 text-sm">
            <Checkbox checked={sel.includes(t.transaction_id)} data-testid={`payout-tx-${t.transaction_id}`}
              onCheckedChange={(v) => setSel(v ? [...sel, t.transaction_id] : sel.filter((x) => x !== t.transaction_id))} />
            <span className="flex-1">{t.title} <span className="text-xs text-stone-400">{formatDate(t.created_at)}</span></span>
            <b>{rupiah(t.provider_net)}</b>
          </label>
        ))}
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Referensi transfer / catatan" className="rounded-xl" data-testid={`payout-ref-${p.provider_id}`} />
        <Button disabled={!sel.length || ref.trim().length < 3} onClick={submit} className="rounded-xl bg-teal-700 hover:bg-teal-800" data-testid={`payout-submit-${p.provider_id}`}>
          Tandai Sudah Dicairkan · {rupiah(total)}
        </Button>
      </div>
    </div>
  );
};

export function AdminPayouts() {
  const [items, setItems] = useState(null);
  const [history, setHistory] = useState([]);
  const load = () => {
    api.get("/admin/payouts").then((r) => setItems(r.data));
    api.get("/admin/payouts/history").then((r) => setHistory(r.data));
  };
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="admin-payouts">
      <AdminHead title="Pencairan Dana" />
      <p className="mb-6 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="payout-manual-note">
        <Info className="mt-0.5 h-4 w-4 shrink-0" /> Transfer bank dilakukan manual di luar aplikasi. Setelah transfer berhasil, tandai entri di sini agar saldo penyedia diperbarui.
      </p>
      {items === null ? <PageLoader /> : items.length === 0 ? <p className="text-sm text-stone-500" data-testid="payouts-empty">Tidak ada saldo yang siap dicairkan.</p> : (
        <div className="grid gap-4 xl:grid-cols-2">{items.map((p) => <ProviderPayout key={p.provider_id} p={p} onDone={load} />)}</div>
      )}
      <h2 className="mb-3 mt-10 font-bold text-stone-900">Riwayat pencairan</h2>
      <Table cols={["Tanggal", "Penyedia", "Rekening", "Jumlah", "Entri", "Referensi"]} testId="payout-history-table" rows={history.map((h) => (
        <tr key={h.payout_id}><Td>{formatDate(h.created_at)}</Td><Td>{h.business_name}</Td><Td>{h.bank_name} {h.bank_account_number}</Td>
          <Td className="font-bold">{rupiah(h.total)}</Td><Td>{h.transaction_ids.length}</Td><Td>{h.reference}</Td></tr>
      ))} />
    </div>
  );
}
